import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import { RequireAuth } from './auth/RequireAuth';
import { useMe } from './auth/useMe';
import { homeParaRoles, ROLES_ALTA_PROVEEDOR } from './auth/types';

// Rutas siempre disponibles sin auth (no vale la pena lazy)
import Login from './pages/Login';
import SinAcceso from './pages/SinAcceso';

// Rutas autenticadas: lazy para bajar el bundle inicial
const Compras = lazy(() => import('./pages/Compras'));
const NuevaSolicitud = lazy(() => import('./pages/NuevaSolicitud'));
const NuevoArticulo = lazy(() => import('./pages/NuevoArticulo'));
const NuevoProveedor = lazy(() => import('./pages/NuevoProveedor'));
const FichaSolicitud = lazy(() => import('./pages/FichaSolicitud'));
const Administracion = lazy(() => import('./pages/Administracion'));
const AdministracionDetalle = lazy(() => import('./pages/AdministracionDetalle'));
const CrearFactura = lazy(() => import('./pages/CrearFactura'));
const Aprobaciones = lazy(() => import('./pages/Aprobaciones'));
const AprobacionDetalle = lazy(() => import('./pages/AprobacionDetalle'));
const Recepcion = lazy(() => import('./pages/Recepcion'));
const RecepcionDetalle = lazy(() => import('./pages/RecepcionDetalle'));
const Configuracion = lazy(() => import('./pages/Configuracion'));
const SolicitudImprimir = lazy(() => import('./pages/SolicitudImprimir'));
const Auditoria = lazy(() => import('./pages/Auditoria'));

function Cargando() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <i className="ph ph-circle-notch animate-spin" />
        Cargando…
      </div>
    </div>
  );
}

/**
 * Home según rol. Un SOLICITANTE arranca en "Compras"; ADMINISTRACION en
 * "Administración"; AUTORIZADOR en "Aprobaciones"; RECEPCION en "Recepción";
 * ADMIN en "Compras". Evita que un rol operativo (por ejemplo RECEPCION)
 * aterrice en un listado de compras que no le corresponde.
 */
function HomeRedirect() {
  const me = useMe();
  if (me.isLoading) return <Cargando />;
  if (!me.data) return <Navigate to="/login" replace />;
  return <Navigate to={homeParaRoles(me.data.roles)} replace />;
}

/**
 * Redirige una ruta vieja de listado a /compras conservando los filtros que
 * traía en la URL (por ejemplo `/mis-compras?estado=BORRADOR`).
 */
function RedirigirACompras({ alcance }: { alcance: 'mias' | 'todas' }) {
  const [sp] = useSearchParams();
  const params = new URLSearchParams(sp);
  if (!params.has('alcance')) params.set('alcance', alcance);
  return <Navigate to={`/compras?${params.toString()}`} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<Cargando />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/sin-acceso" element={<SinAcceso />} />

        <Route
          path="/"
          element={
            <RequireAuth>
              <HomeRedirect />
            </RequireAuth>
          }
        />
        <Route
          path="/compras"
          element={
            <RequireAuth roles={['SOLICITANTE', 'ADMINISTRACION', 'AUTORIZADOR', 'ADMIN']}>
              <Compras />
            </RequireAuth>
          }
        />
        {/* Rutas viejas: «Mis compras», «Órdenes de compra» y «Panel» eran la
            misma consulta con distintos filtros. Se unificaron en /compras; los
            enlaces guardados siguen funcionando y conservan sus filtros. */}
        <Route path="/mis-compras" element={<RedirigirACompras alcance="mias" />} />
        <Route path="/ordenes-compra" element={<RedirigirACompras alcance="todas" />} />
        <Route path="/panel" element={<RedirigirACompras alcance="todas" />} />
        <Route
          path="/solicitudes/nueva"
          element={
            <RequireAuth roles={['SOLICITANTE', 'ADMIN']}>
              <NuevaSolicitud />
            </RequireAuth>
          }
        />
        <Route
          path="/solicitudes/:id/editar"
          element={
            <RequireAuth>
              <NuevaSolicitud />
            </RequireAuth>
          }
        />
        <Route
          path="/articulos/nuevo"
          element={
            <RequireAuth roles={['SOLICITANTE', 'ADMINISTRACION', 'AUTORIZADOR', 'ADMIN']}>
              <NuevoArticulo />
            </RequireAuth>
          }
        />
        <Route
          path="/proveedores/nuevo"
          element={
            <RequireAuth roles={ROLES_ALTA_PROVEEDOR}>
              <NuevoProveedor />
            </RequireAuth>
          }
        />
        <Route
          path="/solicitudes/:id/imprimir"
          element={
            <RequireAuth>
              <SolicitudImprimir />
            </RequireAuth>
          }
        />
        {/* El papel que se le manda al proveedor NO es una pantalla: es un PDF que
            genera el backend (`/api/solicitudes/:id/orden-compra.pdf`). Tener
            además una vista HTML del mismo papel garantizaba que las dos
            divergieran. */}
        <Route
          path="/solicitudes/:id"
          element={
            <RequireAuth>
              <FichaSolicitud />
            </RequireAuth>
          }
        />
        <Route
          path="/administracion"
          element={
            <RequireAuth roles={['ADMINISTRACION', 'ADMIN']}>
              <Administracion />
            </RequireAuth>
          }
        />
        <Route
          path="/administracion/:id"
          element={
            <RequireAuth roles={['ADMINISTRACION', 'ADMIN']}>
              <AdministracionDetalle />
            </RequireAuth>
          }
        />
        <Route
          path="/administracion/:id/factura"
          element={
            <RequireAuth roles={['ADMINISTRACION', 'ADMIN']}>
              <CrearFactura />
            </RequireAuth>
          }
        />
        <Route
          path="/aprobaciones"
          element={
            <RequireAuth roles={['AUTORIZADOR', 'ADMIN']}>
              <Aprobaciones />
            </RequireAuth>
          }
        />
        <Route
          path="/aprobaciones/:id"
          element={
            <RequireAuth roles={['AUTORIZADOR', 'ADMIN']}>
              <AprobacionDetalle />
            </RequireAuth>
          }
        />
        <Route
          path="/recepcion"
          element={
            <RequireAuth roles={['RECEPCION', 'ADMIN']}>
              <Recepcion />
            </RequireAuth>
          }
        />
        <Route
          path="/recepcion/:id"
          element={
            <RequireAuth roles={['RECEPCION', 'ADMIN']}>
              <RecepcionDetalle />
            </RequireAuth>
          }
        />
        <Route
          path="/configuracion"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Configuracion />
            </RequireAuth>
          }
        />
        <Route
          path="/auditoria"
          element={
            <RequireAuth roles={['ADMIN']}>
              <Auditoria />
            </RequireAuth>
          }
        />
        <Route
          path="*"
          element={
            <RequireAuth>
              <HomeRedirect />
            </RequireAuth>
          }
        />
      </Routes>
    </Suspense>
  );
}
